import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../context/AppContext';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import 'dayjs/locale/en-gb';
import './AppointmentRow.css';
import { sanitizeUrl } from '../../utils/functions';
import AuthenticatedImage from '../AuthenticatedImage/AuthenticatedImage';

/**
 * AppointmentRow — a single upcoming-appointment list item.
 *
 * Displays the client's avatar, name + event type, time range + date,
 * and a three-dot context menu with Edit / Delete actions.
 *
 * @component
 * @param {object}   props
 * @param {object}   props.event    - Calendar event object (must contain `client`, `startDate`, `endDate`, `type`).
 * @param {function} props.onEdit   - Called with `event` when the user selects "Edit".
 * @param {function} props.onDelete - Called with `event` when the user selects "Delete".
 */
const AppointmentRow = ({ event, onEdit, onDelete }) => {
    const { t } = useTranslation();
    const { currentLanguage } = useAppContext();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const client       = event.client ?? {};
    const adapterLocale = currentLanguage === 'it' ? 'it' : 'en-gb';
    const start        = dayjs(event.startDate).locale(adapterLocale);
    const end          = dayjs(event.endDate).locale(adapterLocale);
    const timeRange    = `${start.format('HH:mm')} – ${end.format('HH:mm')}`;
    const day          = start.format('ddd, DD MMM');

    // Close the dropdown when the user clicks outside it
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="ar-row">
            {/* Left side: avatar + name/type */}
            <div className='ar-avatar-info-wrapper'>
                {/* Avatar */}
                <AuthenticatedImage
                    className="ar-avatar"
                    src={sanitizeUrl(client.profilePhotoUrl)}
                    fallbackSrc='/images/anonymous_avatar_icon.jpg'
                    alt={`${client.name ?? ''} ${client.surname ?? ''}`}
                />

                {/* Name + event type */}
                <div className="ar-info">
                    <span className="ar-name">
                        {client.name} {client.surname}
                    </span>
                    <span className="ar-type">
                        {event.type ? t(`eventTypes.${event.type}`) : '—'}
                    </span>
                </div>
            </div>

            {/* Time range + day */}
            <div className="ar-time">
                <span className="ar-time-range">{timeRange}</span>
                <span className="ar-day">{day}</span>
            </div>

            {/* Three-dot menu */}
            <div className="ar-menu-wrap" ref={menuRef}>
                <button
                    className="ar-menu-btn"
                    onClick={() => setMenuOpen(o => !o)}
                    aria-label="Options"
                >
                    <span className="material-symbols-outlined">more_vert</span>
                </button>

                {menuOpen && (
                    <div className="ar-menu-dropdown">
                        <button
                            className="ar-menu-item"
                            onClick={() => { setMenuOpen(false); onEdit(event); }}
                        >
                            <span className="material-symbols-outlined">edit</span>
                            {t('edit-event')}
                        </button>
                        <button
                            className="ar-menu-item ar-menu-item-delete"
                            onClick={() => { setMenuOpen(false); onDelete(event); }}
                        >
                            <span className="material-symbols-outlined">delete</span>
                            {t('Delete')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AppointmentRow;
